import React, { useCallback, useContext, useRef } from 'react';
import { TimePicker as AntTimePicker } from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import { TimeFormat } from '../../../infrastructure/types/common/date-types';
import 'dayjs/locale/ru';
import locale from 'antd/es/date-picker/locale/ru_RU';
import styled from 'styled-components';
import { colorsPalette } from '../../styles/colors-palette';
import { ValidationContext } from './Validation';
import { RecursiveEnumerateType } from 'main-service/infrastructure/types/features/projects/projects-types';

const DEFAULT_TIME_FORMAT = 'HH:mm';

interface TimeRangePickerProps {
  value?: [FormattedTime | null, FormattedTime | null];
  format?: TimeFormat;
  disabled?: boolean;
  onChange?: (period: RangeValue) => void;
  size?: RangeSizeType;
  placeholder?: [string, string];
  className?: string;
  allowClear?: boolean;
  minuteStep?: MinuteStep;
}

const { RangePicker: AntRangePicker } = AntTimePicker;

type RangeValue = [FormattedTime | null, FormattedTime | null] | null;
type AntRangeValue = [Dayjs | null, Dayjs | null] | null;
type RangeSizeType = 'large' | 'middle' | 'small';
type RangeMinuteAntType<F extends number, T extends number> =
  | Exclude<RecursiveEnumerateType<T>, RecursiveEnumerateType<F>>
  | T;
type MinuteStep = RangeMinuteAntType<1, 58> | undefined;

function TimeRangePicker({
  value,
  disabled,
  onChange,
  format,
  size,
  placeholder,
  className,
  allowClear,
  minuteStep,
}: TimeRangePickerProps): JSX.Element {
  const { validateStatus } = useContext(ValidationContext);
  const containerRef = useRef<HTMLDivElement>(null);

  const formatRawTimeInput = (raw: string): string | null => {
    const digits = raw.replace(/\D/g, '');
    if (digits.length === 0) return null;

    let hours: string;
    let minutes: string;

    if (digits.length <= 2) {
      hours = digits.padStart(2, '0');
      minutes = '00';
    } else if (digits.length === 3) {
      hours = '0' + digits[0];
      minutes = digits.slice(1, 3);
    } else {
      hours = digits.slice(0, 2);
      minutes = digits.slice(2, 4);
    }

    const h = Math.min(parseInt(hours, 10), 23);
    const m = Math.min(parseInt(minutes, 10), 59);
    return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
  };

  const antOnChange = (values: AntRangeValue, formatString: [string, string]) => {
    if (values !== undefined) {
      if (values === null) {
        onChange && onChange(null);
        return;
      }

      onChange &&
        onChange([
          (formatString[0] as FormattedTime) ?? null,
          (formatString[1] as FormattedTime) ?? null,
        ]);
    }
  };

  const handleBlur = useCallback(() => {
    if (!containerRef.current || !onChange) return;

    const inputs = containerRef.current.querySelectorAll<HTMLInputElement>('input');
    if (inputs.length < 2) return;

    const rawFrom = inputs[0].value;
    const rawTo = inputs[1].value;

    const currentFrom = value?.[0] ?? null;
    const currentTo = value?.[1] ?? null;

    const formattedFrom = rawFrom ? formatRawTimeInput(rawFrom) : null;
    const formattedTo = rawTo ? formatRawTimeInput(rawTo) : null;

    if (formattedFrom !== currentFrom || formattedTo !== currentTo) {
      onChange([(formattedFrom as FormattedTime) ?? null, (formattedTo as FormattedTime) ?? null]);
    }
  }, [onChange, value]);

  let antValue: AntRangeValue = [null, null];

  if (value) {
    antValue = [
      value[0] ? dayjs(value[0], format ?? DEFAULT_TIME_FORMAT) : null,
      value[1] ? dayjs(value[1], format ?? DEFAULT_TIME_FORMAT) : null,
    ];
  }

  return (
    <div ref={containerRef} onBlur={handleBlur}>
      <StyledRangePicker
        value={antValue}
        format={format ? format : DEFAULT_TIME_FORMAT}
        onChange={(values, formatted) => antOnChange(values, formatted)}
        changeOnBlur={true}
        needConfirm={false}
        disabled={disabled}
        locale={locale}
        allowClear={allowClear ?? false}
        size={size ? size : 'large'}
        status={validateStatus}
        placeholder={placeholder}
        className={className}
        minuteStep={minuteStep}
      />
    </div>
  );
}

const StyledRangePicker = styled(AntRangePicker)`
  &.ant-picker {
    width: 100%;

    :hover {
      border-color: ${colorsPalette.dustyBlue500};
    }
  }

  &.ant-picker-focused {
    border-color: ${colorsPalette.dustyBlue500};
  }

  &.ant-picker-range .ant-picker-active-bar {
    background-color: ${colorsPalette.dustyBlue500};
  }
`;

export default TimeRangePicker;
